/**
 * LazyComponent Wrapper
 *
 * Wraps lazy-loaded components with Suspense and error boundary
 */

import { Suspense, lazy, type ReactNode } from 'react'
import type React from 'react'

interface LazyComponentProps {
  /** The lazy-loaded component function */
  component: () => Promise<{ default: React.ComponentType<any> }>

  /** Fallback UI while component loads */
  fallback?: ReactNode

  /** Page title (for browser tab) */
  pageTitle?: string
}

/**
 * Default loading fallback component
 * Minimal and reusable across all routes
 */
export function RouteLoadingFallback(): JSX.Element {
  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="space-y-4 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
        <p className="text-sm text-muted-foreground">Loading page...</p>
      </div>
    </div>
  )
}

/**
 * Cache for lazy components to avoid recreating them on every render.
 * React's lazy() should only be called once per component factory,
 * otherwise it creates a new component identity each time causing
 * infinite re-renders.
 */
const lazyComponentCache = new Map<
  () => Promise<{ default: React.ComponentType<any> }>,
  React.LazyExoticComponent<React.ComponentType<any>>
>()

/**
 * Get or create a cached lazy component
 */
function getCachedLazyComponent(
  componentFactory: () => Promise<{ default: React.ComponentType<any> }>
): React.LazyExoticComponent<React.ComponentType<any>> {
  let cachedComponent = lazyComponentCache.get(componentFactory)
  if (!cachedComponent) {
    cachedComponent = lazy(componentFactory)
    lazyComponentCache.set(componentFactory, cachedComponent)
  }
  return cachedComponent
}

/**
 * Wrapper for lazy-loaded components
 *
 * Provides consistent Suspense behavior and error handling
 * across all route components
 *
 * @example
 * ```tsx
 * <LazyComponent
 *   component={() => import('../pages/HomePage')}
 *   pageTitle="Home"
 * />
 * ```
 */
export function LazyComponent({
  component,
  fallback = <RouteLoadingFallback />,
  pageTitle,
}: LazyComponentProps): JSX.Element {
  // Use cached lazy component to maintain stable component identity
  const Component = getCachedLazyComponent(component)

  // Update page title when component loads (optional)
  if (pageTitle) {
    // Note: Better to handle in component itself, but available here if needed
  }

  return (
    <Suspense fallback={fallback}>
      <Component />
    </Suspense>
  )
}
